import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Local Resources
import StringBatteryMap from '../components/battery_map/string_batterymap';
import axiosInstance from '../components/axios';
import StringIcon from '../components/battery_map/string_icon';
import SystemIcon from '../components/battery_map/system_icon';
import TempIcon from '../components/battery_map/temp_icon';
import AuxIcon from '../components/battery_map/aux_icon';

// Material UI
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import { Typography } from '@mui/material';

function BatteryDataLoading(Component) {
	return function DataLoadingComponent({ isLoading, ...props }) {
		if (!isLoading) return <Component {...props} />;
		return (
			<Paper
				padding='true'
				sx={{
					boxShadow: '0',
					bgcolor: '#0F1820',
					overflow: 'auto',
					height: "100%",
					width: '100%',
					minWidth: 0,
				}}
			>
				<Typography
					variant='h5'
					fontFamily='Saira, sans-serif'
					fontWeight='600'
					color='#FFFFFF'
					align='center'
					padding='true'
				>
					Loading...
				</Typography>
				<Grid container sx={{paddingX: '5px'}}>
					<Grid item>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={64} height={80}/>
					</Grid>
					<Grid item>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={64} height={80}/>
					</Grid>
					<Grid item>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={64} height={80}/>
					</Grid>
					<Grid item>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={64} height={80}/>
					</Grid>
					<Grid item>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={64} height={80}/>
					</Grid>
					<Grid item>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={64} height={80}/>
					</Grid>
					<Grid item>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={64} height={80}/>
					</Grid>
					<Grid item>
						<Skeleton sx={{ mx: 1, my: 1 }} variant="rounded" width={64} height={80}/>
					</Grid>
				</Grid>
				<Box
					display='flex'
					flexDirection='row'
					flexGrow='true'
					minHeight='50vh'
				/>
			</Paper>
		);
	};
}

function BatteryMap(props) {
	const {
		locationNum,
		locationName,
		numStrings,
		enableFlash,
		includeAlarms,
		showRealtime,
		measurementsInUnits,
		statusData,
		stringsData,
	} = props;
	const [ activeString, setActiveString ] = useState(1);
	const [mapScale, setMapScale] = useState(1.0);

	const navigate = useNavigate();

	const handleSystemClick = () => {
		// Navigate to the system Trend page
		navigate(`/location/${locationNum}/trend/system/`);
	};

	const handleStringClick = (stringNum) => {
		if (activeString !== stringNum) {
			// Activate the string clicked
			setActiveString(stringNum);
		} else {
			// If the same string is clicked again, navigate to the string Trend page
			navigate(`/location/${locationNum}/trend/string?string=${stringNum}`);
		}
	};

	const handleTempClick = () => {
		// Navigate to the system Trend page - eventually link to temp graph page
		navigate(`/location/${locationNum}/trend/system/`);
	};

	return (
		<Paper
			padding="true"
			sx={{
				boxShadow: "0",
				bgcolor: "#0F1820",
				overflow: 'auto',
				height: "100%",
				width: '100%',
				minWidth: 0,
			}}
		>
			<Typography
				variant="h5"
				fontFamily="Saira, sans-serif"
				fontWeight="600"
				color="#FFFFFF"
				align="center"
				padding="5px"
			>
				{locationName}
			</Typography>
			<Grid
				container
				sx={{
					paddingX: "5px",
					marginBottom: "25px",
					justifyContent: "center",
				}}
			>
				{/* System Icon */}
				<Grid item>
					<SystemIcon systemState={statusData} onClick={handleSystemClick} scale={mapScale}/>
				</Grid>

				{/* String Icons */}
				<Grid
					item
					sx={{
						display: "flex",
						flexDirection: "row",
						marginTop: "50px",
					}}
				>
					{stringsData.map((sysString) => (
						<Box key={sysString.string_num} sx={{ marginX: "2px" }}>
							<StringIcon
								isActive={activeString === sysString.string_num}
								locationNum={locationNum}
								stringData={sysString}
								onClick={handleStringClick}
								scale={mapScale}
							/>
						</Box>
					))}
				</Grid>

				{/* Temp Icon */}
				<Grid item sx={{ marginLeft: "25px" }}>
					<TempIcon
						ambientState={statusData.ambient_temp}
						tempsState={[]}
						onClick={handleTempClick}
						scale={mapScale}
					/>
				</Grid>
				<Grid item>
					<AuxIcon
						auxState={[]}
						scale={mapScale}
					/>
				</Grid>
			</Grid>
			<StringBatteryMap locationNum={locationNum} stringNum={activeString} scale={mapScale} />
		</Paper>
	);
}

export default function LoadBatteryMap() {
	
	const { locationNum } = useParams();

	const BatteryMapLoading = BatteryDataLoading(BatteryMap);
	const [systemState, setSystemState] = useState({
		isLoading: true,
		locationNum: locationNum,
		locationName: "",
		numStrings: 1,
		enableFlash: true,
		includeAlarms: false,
		showRealtime: false,
		measurementsInUnits: false,
		statusData: [],
		stringsData: [],
	});

	const navigate = useNavigate();
	
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/status/system`)
			.then(res => {
				const systemData = res.data;
				setSystemState({
					isLoading: false,
					locationNum: locationNum,
					locationName: systemData.location_name,
					numStrings: systemData.num_strings,
					enableFlash: systemData.enable_flash === 1,
					includeAlarms: systemData.include_alarms === 1,
					showRealtime: systemData.show_realtime === 1,
					measurementsInUnits: systemData.meas_in_units === 1,
					statusData: systemData.status,
					stringsData: systemData.strings,
				})
			})
			.catch(err => {
				console.error("Error fetching system status data: ", err);
				navigate('/404');
			});
	}, [setSystemState]);

	return (
		<BatteryMapLoading
			isLoading={systemState.isLoading}
			locationNum={systemState.locationNum}
			locationName={systemState.locationName}
			numStrings={systemState.numStrings}
			enableFlash={systemState.enableFlash}
			includeAlarms={systemState.includeAlarms}
			showRealtime={systemState.showRealtime}
			measurementsInUnits={systemState.measurementsInUnits}
			statusData={systemState.statusData}
			stringsData={systemState.stringsData}
		/>
	);
}